/**
 * AddLinkDialog - Dialog for adding new links to Text.parameters.links
 *
 * Provides name + URL inputs for external link creation.
 *
 * Sprint: screen-editor-body
 * Task: Phase 3 - AddLinkDialog
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import type { LinkType } from '../../../types/editorFieldTypes';
import type { PredecessorScreen } from '@modules/reactFlow/types/predecessorScreen.types';

/**
 * Link data structure
 */
export interface LinkData {
  type: LinkType;
  url: string;
}

/**
 * Props for AddLinkDialog
 */
export interface AddLinkDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Callback to close dialog */
  onClose: () => void;

  /** Callback when link is added/edited - receives name and LinkData */
  onAdd: (name: string, linkData: LinkData) => void;

  /** Existing link keys to prevent duplicates */
  existingKeys: string[];

  /** @deprecated Kept for backward compatibility — not rendered */
  predecessorScreens?: PredecessorScreen[];

  /** Dialog mode: 'add' or 'edit' (default: 'add') */
  mode?: 'add' | 'edit';

  /** Key being edited (required when mode='edit') */
  editingKey?: string;

  /** Initial URL for edit mode pre-fill */
  initialUrl?: string;
}

/**
 * AddLinkDialog - dialog for adding links with name + URL
 *
 * Layout:
 * - Name input
 * - URL input
 * - Cancel / Add buttons
 */
export const AddLinkDialog: React.FC<AddLinkDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingKeys,
  mode = 'add',
  editingKey,
  initialUrl,
}) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [url, setUrl] = useState('');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && editingKey) {
        setName(editingKey);
        setNameError(null);
        setUrl(initialUrl || '');
      } else {
        setName('');
        setNameError(null);
        setUrl('');
      }
    }
  }, [open, mode, editingKey, initialUrl]);

  // Validate name
  const validateName = useCallback(
    (value: string): string | null => {
      const trimmed = value.trim();

      if (!trimmed) {
        return null; // Empty is not an error, just prevents submission
      }

      // In edit mode, allow keeping the original name (exclude editingKey from check)
      const keysToCheck =
        mode === 'edit' && editingKey
          ? existingKeys.filter((k) => k !== editingKey)
          : existingKeys;

      if (keysToCheck.includes(trimmed)) {
        return `"${trimmed}" already exists`;
      }

      return null;
    },
    [existingKeys, mode, editingKey]
  );

  // Handle name change
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setName(value);
      setNameError(validateName(value));
    },
    [validateName]
  );

  // Handle URL change
  const handleUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
  }, []);

  // Check if form is valid
  const isValid = useMemo(() => {
    const hasName = name.trim().length > 0;
    const hasNoNameError = !nameError;
    const hasUrl = url.trim().length > 0;
    return hasName && hasNoNameError && hasUrl;
  }, [name, nameError, url]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!isValid) return;

    onAdd(name.trim(), {
      type: 'link' as LinkType,
      url: url.trim(),
    });
    onClose();
  }, [isValid, name, url, onAdd, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle Enter key
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isValid) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [isValid, handleSubmit]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>{mode === 'edit' ? 'Edit Link' : 'Add Link'}</DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Name Input */}
          <TextField
            autoFocus
            fullWidth
            label="Link Name"
            placeholder="e.g., terms, privacy, support..."
            value={name}
            onChange={handleNameChange}
            onKeyPress={handleKeyPress}
            error={!!nameError}
            helperText={nameError}
            size="small"
            data-testid="link-name-input"
            inputProps={{
              'aria-label': 'Link Name',
            }}
          />

          {/* URL Input */}
          <TextField
            fullWidth
            label="URL"
            placeholder="https://example.com/page"
            value={url}
            onChange={handleUrlChange}
            onKeyPress={handleKeyPress}
            size="small"
            data-testid="link-url-input"
            inputProps={{
              'aria-label': 'URL',
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid} data-testid="link-submit-btn">
          {mode === 'edit' ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLinkDialog;
